import { AlarmLevel } from './alarm-level';
import {
    feedbackAlarmDefaults,
    hAlarmDefaults,
    hhAlarmDefaults,
    lAlarmDefaults,
    llAlarmDefaults,
    sensorFailureDefaults,
} from './channel-defaults';
import { ChannelType, isDigitalContact, isDigitalMotor, isSystemChannel } from './channel-type';
import { MotorAlarmSetting, MotorType } from './motor-alarm-setting';
import { AlarmPropertyList } from './repose-group';

export class AlarmSetting {
    enable: boolean;
    statusString: string;
    statusStringContactOn?: string;
    statusStringContactOff?: string;
    setPoint: number;
    alarmGroupNo: number;
    reposeGroup1No: number;
    reposeGroup2No: number;
    manualReposeFlag: boolean;
    delayTimer: number;
    normalOpenFlag?: boolean;
    alarmLevel: AlarmLevel;
}

export class DigitalAlarmSetting {
    enable: boolean;
    statusStringContactOn: string;
    statusStringContactOff: string;
    alarmGroupNo: number;
    reposeGroup1No: number;
    reposeGroup2No: number;
    manualReposeFlag: boolean;
    delayTimer: number;
    normalOpenFlag: boolean;
    alarmLevel: AlarmLevel;
    statusString?: string;
}

export const AlarmSettingFns = {
    create,
    createDigital,
    clone,
    createDefaultSetting,
};

function create(isNull = false): AlarmSetting {
    return {
        enable: false,
        statusString: '',
        setPoint: 0,
        alarmGroupNo: 0,
        reposeGroup1No: 0,
        reposeGroup2No: 0,
        manualReposeFlag: false,
        delayTimer: 0,
        normalOpenFlag: false,
        alarmLevel: isNull ? AlarmLevel.None : AlarmLevel.Alarm,
    };
}

function createDefaultSetting(
    enabled: boolean,
    chanelType: ChannelType,
    alarmType: AlarmPropertyList,
    currentSetting: AlarmSetting | DigitalAlarmSetting | MotorAlarmSetting
): AlarmSetting | DigitalAlarmSetting | MotorAlarmSetting {
    if (isDigitalContact(chanelType)) {
        const s = currentSetting as DigitalAlarmSetting;
        return {
            enable: enabled,
            statusStringContactOn: s.statusStringContactOn || (enabled ? 'ON' : ''),
            statusStringContactOff: s.statusStringContactOff || (enabled ? 'OFF' : ''),
            alarmGroupNo: s.alarmGroupNo ?? (enabled ? 1 : 0),
            reposeGroup1No: s.reposeGroup1No ?? 0,
            reposeGroup2No: s.reposeGroup2No ?? 0,
            manualReposeFlag: s.manualReposeFlag ?? false,
            delayTimer: s.delayTimer ?? 0,
            normalOpenFlag: s.normalOpenFlag ?? true,
            alarmLevel: s.alarmLevel ?? AlarmLevel.Alarm,
        };
    } else if (isDigitalMotor(chanelType)) {
        const s = currentSetting as MotorAlarmSetting;
        return {
            enable: enabled,
            motorType: s.motorType || (enabled ? MotorType.RUN : MotorType.UNDEFINED),
            stopStatusString: s.stopStatusString || (enabled ? 'STOP' : ''),
            stbyStatusString: s.stbyStatusString ?? '',
            runStatusString1: s.runStatusString1 || (enabled ? 'RUN' : ''),
            runStatusString2: s.runStatusString2 ?? '',
            runStatusString3: s.runStatusString3 ?? '',
            runStatusString4: s.runStatusString4 ?? '',
            runStatusString5: s.runStatusString5 ?? '',
            alarmGroupNo: s.alarmGroupNo || (enabled ? 1 : 0),
            reposeGroup1No: s.reposeGroup1No ?? 0,
            reposeGroup2No: s.reposeGroup2No ?? 0,
            delayTimer: s.delayTimer ?? 0,
            alarmLevel: s.alarmLevel || (enabled ? AlarmLevel.Alarm : AlarmLevel.None),
        };
    } else {
        const defaultSetting = getDefaultSetting(alarmType);
        return {
            enable: enabled,
            statusString: enabled ? (defaultSetting ? defaultSetting.status : '') : '',
            setPoint: enabled ? (defaultSetting ? defaultSetting.setPoint : 0) : 0,
            alarmGroupNo: enabled ? (defaultSetting ? defaultSetting.alarmGroupNo : 0) : 0,
            reposeGroup1No: enabled ? (defaultSetting ? defaultSetting.reposeGroup1No : 0) : 0,
            reposeGroup2No: enabled ? (defaultSetting ? defaultSetting.reposeGroup2No : 0) : 0,
            manualReposeFlag: false,
            delayTimer: enabled ? (defaultSetting ? defaultSetting.delay : 0) : 0,
            normalOpenFlag: false,
            alarmLevel: enabled ? (defaultSetting ? defaultSetting.alarm : AlarmLevel.Alarm) : AlarmLevel.Alarm,
        };
    }
}

function getDefaultSetting(alarmType: AlarmPropertyList) {
    if (alarmType == AlarmPropertyList.alarmSettingVeryHigh) return hhAlarmDefaults;
    else if (alarmType == AlarmPropertyList.alarmSettingHigh) return hAlarmDefaults;
    else if (alarmType == AlarmPropertyList.alarmSettingLow) return lAlarmDefaults;
    else if (alarmType == AlarmPropertyList.alarmSettingVeryLow) return llAlarmDefaults;
    else if (alarmType == AlarmPropertyList.alarmSettingSensorFail) return sensorFailureDefaults;
    else if (alarmType == AlarmPropertyList.alarmSettingFeedbackError) return feedbackAlarmDefaults;
}

function createDigital(): DigitalAlarmSetting {
    return {
        enable: false,
        statusStringContactOn: '',
        statusStringContactOff: '',
        alarmGroupNo: 0,
        reposeGroup1No: 0,
        reposeGroup2No: 0,
        manualReposeFlag: false,
        delayTimer: 0,
        normalOpenFlag: true,
        alarmLevel: AlarmLevel.Alarm,
    };
}

function clone(alarm: AlarmSetting): AlarmSetting {
    return {
        enable: alarm.enable,
        statusString: alarm.statusString,
        setPoint: alarm.setPoint,
        alarmGroupNo: alarm.alarmGroupNo,
        reposeGroup1No: alarm.reposeGroup1No,
        reposeGroup2No: alarm.reposeGroup2No,
        manualReposeFlag: alarm.manualReposeFlag,
        delayTimer: alarm.delayTimer,
        normalOpenFlag: alarm.normalOpenFlag,
        alarmLevel: alarm.alarmLevel,
    };
}
